import { AnimatePresence, motion, Variants } from 'framer-motion';
import React, { FC, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useObserver } from '../../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../../Legacy';
import { Fill } from '../../../ui/Fill';
import { useResolve } from '../../Container';
import { AlertInstance, AlertManager } from './AlertManager';
import { AlertStyleAnnouncement } from './AlertStyleAnnouncement';
import { AlertStyleDefault } from './AlertStyleDefault';
import { AlertStyleDialog } from './AlertStyleDialog';
import { AlertStyleError } from './AlertStyleError';

const alertAnimation: Variants = {
    before: {
        opacity: 0,
        scale: .5
    },
    during: {
        opacity: 1,
        scale: 1.0,
        transition: {
            opacity: { duration: .4, ease: 'linear', },
            scale: { duration: .5, ease: [0, .99, .2, 1.14], },
        }
    },
    after: {
        opacity: 0,
        y: '-4rem',
        transition: {
            ease: 'easeOut',
            duration: .4,
        }
    }
};

const AlertLane = React.memo<{ children?: ReactNode }>(({ children }) => {
    const elmt = useRef<HTMLDivElement>();

    const style = {
        position: 'relative' as const,
        marginTop: '1.5rem',
    };

    const onSetHeight = useCallback(() => {

        const al = elmt.current;
        const ab = al.querySelector('[data-border]');
        if (ab) {
            al.style.height = `${ab.clientHeight}px`;
        }
        else {
            al.style.height = 'initial';
        }

    }, [elmt.current]);

    useEffect(() => {
        if (!elmt.current) return;
        const observer = new MutationObserver(onSetHeight);
        observer.observe(elmt.current, {
            childList: true,
            subtree: true,
        });
        onSetHeight();
        return () => observer.disconnect();
    }, [elmt.current, onSetHeight]);

    return <div style={style} ref={elmt}>
        {children}
    </div>;
});

const AlertFrame = styled.div`
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-16rem);
    width: 32rem;
`;

const AlertBorder = styled.div`
    position: relative;
    margin-bottom: 1rem;
    box-sizing: border-box;
    border-radius: 0.5rem;
    /* box-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0.2), 0 15px 15px 0 rgba(0, 0, 0, 0.05); */
    box-shadow: 0 1.25rem 1.5rem -0.375rem rgb(0 0 0 / 10%), 0 0.625rem 0.625rem -0.375rem rgb(0 0 0 / 4%);
    overflow: hidden;
    background: white;
    pointer-events: all;

    em {
        font-style: italic;
    }
    
    strong {
        font-weight: 500;
    }
`;

type AlertStyle = FC<{ clickable: boolean, delay: number }>;
const AlertStyles: Record<string, AlertStyle> = {
    'announcement': AlertStyleAnnouncement,
    'default': AlertStyleDefault,
    'dialog': AlertStyleDialog,
    'error': AlertStyleError,
};

const AlertStack: LFC<{ instances: AlertInstance[] }> = ({ instances }) => {
    return <AnimatePresence>
        {instances.map(inst => (
            <motion.div key={inst.id} variants={alertAnimation} initial="before" animate="during" exit="after">
                <AlertMount instance={inst} />
            </motion.div>
        ))}
    </AnimatePresence>;
};

const AlertMount: LFC<{ instance: AlertInstance, onDismiss?: (id: string) => void }> = ({ instance }) => {
    const { id, type, modal, delay, body: Body } = instance;
    const alerts = useResolve(AlertManager);

    const onClose = useCallback(() => alerts.close(id), [id]);
    const onClick = useCallback(() => {
        if (!modal) alerts.close(id);
    }, [modal]);

    const Style = useMemo(() => AlertStyles[type], [type]);

    return <AlertFrame onClick={onClick}>
        <AlertBorder data-border>
            <Style {...{ clickable: !modal, delay }}>
                <Body onClose={onClose} />
            </Style>
        </AlertBorder>
    </AlertFrame>;
};

export const AlertLayer = () => {

    const all = useObserver(useResolve(AlertManager));
    const [modal, modeless] = useMemo(() => [all.filter(x => !!x.modal), all.filter(x => !x.modal)], [all]);

    return <Fill id="alert-layer" pointerEvents="none" zIndex="1">
        <AlertLane>
            <AlertStack instances={modal} />
        </AlertLane>
        <AlertLane>
            <AlertStack instances={modeless} />
        </AlertLane>
    </Fill>;
};
